// Shared navigation model for the marketing site.
// Route targets live here so the header, footer and homepage cards stay in sync.

export const ROUTES = {
  happensToOtherPeople: '/happens-to-other-people',
  neurodivergentExplained: '/neurodivergent-explained',
  predatorProfile: '/predator-profile',
  sanTzu: '/san-tzu',
  emotionalManipulation: '/emotional-manipulation',
  abusePlaybook: '/abuse-playbook',
  seniorChallenge: '/senior-challenge',
  familyImpact: '/family-impact',
  cashCow: '/cash-cow',
  badDream: '/bad-dream',
  dataPrivacy: '/data-privacy',
  // Trust Center is the Data Privacy page; no separate mockup exists.
  trustCenter: '/data-privacy',
  resources: '/resources',
  investors: '/investors',
  leadership: '/leadership',
  careers: '/careers',
  philanthropy: '/philanthropy',
  philanthropyGrants: '/philanthropy/grants',
  legal: '/legal',
  terms: '/terms',
  cookies: '/cookies',
  trademark: '/trademark',
  signIn: '/signin',
  getStarted: '/get-access',
};

// "All NuVola AI" mega-menu columns — also reused by the site footer.
export const ALL_NUVOLA_COLUMNS = [
  {
    heading: 'Trending',
    links: [
      { label: 'Trust Center', to: ROUTES.trustCenter },
      { label: 'Resources', to: ROUTES.resources },
    ],
  },
  {
    heading: 'About',
    links: [
      { label: 'Investors', to: ROUTES.investors },
      { label: 'Leadership', to: ROUTES.leadership },
      { label: 'Career Opportunities', to: ROUTES.careers },
      { label: 'Philanthropy Spotlight', to: ROUTES.philanthropy },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Terms of Service', to: ROUTES.terms },
      { label: 'Cookie preference', to: ROUTES.cookies },
      { label: 'Trademark and copyright', to: ROUTES.trademark },
    ],
  },
];

// Content sections. Sub-pages swap the homepage's topic nav for these three,
// with the section containing the current page marked active.
export const SECTIONS = [
  {
    id: 'noscam',
    label: 'No / SCAM',
    links: [
      { label: 'Happens to other people', to: ROUTES.happensToOtherPeople },
      { label: 'Neurodivergent Explained', to: ROUTES.neurodivergentExplained },
      { label: 'Predator Profile', to: ROUTES.predatorProfile },
      { label: 'Sun Tzu', to: ROUTES.sanTzu },
    ],
  },
  {
    id: 'brainwashing',
    label: 'Brainwashing',
    links: [
      { label: 'Emotional Manipulation', to: ROUTES.emotionalManipulation },
      { label: 'Abuse Playbook', to: ROUTES.abusePlaybook },
    ],
  },
  {
    id: 'wreckage',
    label: 'Human Wreckage',
    links: [
      { label: 'The Senior Challenge', to: ROUTES.seniorChallenge },
      { label: 'The Family Impact', to: ROUTES.familyImpact },
      { label: 'Cash Cow', to: ROUTES.cashCow },
      { label: 'Bad Dream', to: ROUTES.badDream },
    ],
  },
];

// Footer sitemap columns (screenshot: main-homepage 9.32.35). 10-1: the
// headings take the nav bar's own names, so the directory, the footer and the
// nav all read the same.
export const FOOTER_COLUMNS = [
  {
    heading: 'Paradigm shift',
    links: [
      { label: 'Happens to other people', to: ROUTES.happensToOtherPeople },
      { label: 'Neurodivergent Explained', to: ROUTES.neurodivergentExplained },
      { label: 'Scammer Profile', to: ROUTES.predatorProfile },
      { label: 'Art of War', to: ROUTES.sanTzu },
    ],
  },
  {
    heading: 'Brainwashing',
    links: [
      { label: 'Emotional Manipulation', to: ROUTES.emotionalManipulation },
      { label: 'Abuse Playbook', to: ROUTES.abusePlaybook },
      { label: 'The Senior Challenge', to: ROUTES.seniorChallenge },
      { label: 'Family Pain', to: ROUTES.familyImpact },
    ],
  },
  {
    heading: 'Human Wreckage',
    links: [
      { label: 'Cash Cow', to: ROUTES.cashCow },
      { label: 'Bad Dreams', to: ROUTES.badDream },
    ],
  },
];

// Breadcrumb rendered under the nav bar on every sub-page.
//
// Explicit rather than derived from the columns above: the trail still opens
// "No / SCAM" where the directory column is headed "Paradigm shift", and two
// routes are shared by two directory entries (/leadership is both Investors and
// Leadership, /data-privacy is both Trust Center and Data Privacy), so a
// generated map would pick the wrong label for one of each pair.
export const PAGE_TRAILS = {
  [ROUTES.happensToOtherPeople]: ['No / SCAM', 'Happens to other people'],
  [ROUTES.neurodivergentExplained]: ['No / SCAM', 'Neurodivergent Explained'],
  [ROUTES.predatorProfile]: ['No / SCAM', 'Scammer Profile'],
  [ROUTES.sanTzu]: ['No / SCAM', 'Art of War'],
  [ROUTES.emotionalManipulation]: ['Brainwashing', 'Emotional Manipulation'],
  [ROUTES.abusePlaybook]: ['Brainwashing', 'Abuse Playbook'],
  [ROUTES.seniorChallenge]: ['Brainwashing', 'The Senior Challenge'],
  [ROUTES.familyImpact]: ['Brainwashing', 'Family Pain'],
  [ROUTES.cashCow]: ['Human Wreckage', 'Cash Cow'],
  [ROUTES.badDream]: ['Human Wreckage', 'Bad Dream'],
  [ROUTES.dataPrivacy]: ['Trending', 'Trust Center'],
  [ROUTES.resources]: ['Trending', 'Resources'],
  [ROUTES.investors]: ['About', 'Investors'],
  [ROUTES.leadership]: ['About', 'Leadership'],
  [ROUTES.careers]: ['About', 'Career Opportunities'],
  [ROUTES.philanthropy]: ['About', 'Philanthropy Spotlight'],
  [ROUTES.philanthropyGrants]: ['About', 'Philanthropy Spotlight', 'Philanthropy Grants'],
  [ROUTES.legal]: ['Legal'],
  [ROUTES.terms]: ['Legal', 'Terms of Service'],
  [ROUTES.cookies]: ['Legal', 'Cookie preference'],
  [ROUTES.trademark]: ['Legal', 'Trademark and copyright'],
};

// Sub-page nav: the three content sections plus the directory, per the 8-22
// mockup. Membership comes from the footer columns so the nav, the footer and
// the breadcrumbs can't drift; only the first label differs, because the
// breadcrumbs say "No / SCAM" where the footer column says "Paradigm shift".
export const SECTION_MENUS = [
  { id: 'sec-noscam', label: 'No / SCAM', links: FOOTER_COLUMNS[0].links },
  { id: 'sec-brainwashing', label: 'Brainwashing', links: FOOTER_COLUMNS[1].links },
  { id: 'sec-wreckage', label: 'Human Wreckage', links: FOOTER_COLUMNS[2].links },
];
